'use client'

import { type CSSProperties, type ReactNode } from 'react'
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Box,
  Card,
  Group,
  Text,
  Tooltip,
} from '@mantine/core'
import {
  IconCornerDownRight,
  IconPaperclip,
  IconPhoto,
  IconQuote,
  IconRobot,
  IconVideo,
} from '@/components/icons'
import {
  MessageChannelDeliveryStatus,
} from '@/components/tickets/MessageChannelDeliveryStatus'
import { TicketMessageAttachments } from '@/components/tickets/TicketMessageAttachments'
import {
  CLASSIC_MSG_SHADOW_DARK,
  CLASSIC_MSG_TEXT,
  CLASSIC_MSG_TEXT_DARK_ON_BUBBLE,
  CLASSIC_MSG_TIME_DARK,
  CLASSIC_MSG_TIME_LIGHT,
  LOCALE_LABELS,
  MESSAGE_BADGE_COLOR,
  MESSAGE_CLASSIC,
  displayUser,
  formatAttachmentFileSize,
  getMessageVisualKind,
  initialsFromSenderFallback,
  isAssignmentSystemPseudoMessage,
  quoteInBubbleAuthorLabel,
  quotePreviewAuthorLabel,
  resolveDisplayDeliveryStatus,
  resolveQuotedForMessage,
  shouldShowChannelDeliveryStatus,
  translationBlockForMessage,
  workspaceUserInitials,
} from '@/components/tickets/ticket-message-utils'
import { formatWhen } from '@/lib/ticket-display'
import { useTheme } from '@/lib/theme-context'
import { resolveUploadsUrl } from '@/lib/uploads'
import { resolveOperatorAvatarSrc } from '@/lib/dashboard-ai-operator'
import type {
  TicketDetailMessage,
  TicketDetailQuotedMessage,
  WorkspaceUserRow,
} from '@/types/api'

export const THREAD_MESSAGE_FONT_SIZE = 'calc(var(--mantine-font-size-xs) + 2pt)'

export {
  displayUser,
  formatAttachmentFileSize,
  initialsFromSenderFallback,
  isAssignmentSystemPseudoMessage,
  quotePreviewAuthorLabel,
  resolveQuotedForMessage,
  workspaceUserInitials,
}

/** Текст сообщения или плейсхолдеры Telegram (как formatMessageContent в старом фронте). */
function MessageBodyContent({
  content,
  bodyStyle,
}: {
  content: string
  /** Цвет текста тела как в classic `.message-content` (светлая / тёмная тема). */
  bodyStyle?: CSSProperties
}) {
  const t = (content || '').trim()
  const photo = t.match(/^\[TELEGRAM_PHOTO:([^\]]+)]$/)
  if (photo) {
    return (
      <Alert color="gray" variant="light" p="xs" mt={4}>
        <Group gap={8} wrap="nowrap">
          <IconPhoto size={20} stroke={1.5} style={{ flexShrink: 0 }} />
          <Box style={{ minWidth: 0 }}>
            <Text size="xs" fw={600}>
              Изображение из Telegram
            </Text>
            <Text size="xs" c="dimmed" lineClamp={2}>
              File ID: {photo[1]}
            </Text>
          </Box>
        </Group>
      </Alert>
    )
  }
  const vnote = t.match(/^\[TELEGRAM_VIDEO_NOTE:([^\]]+)]$/)
  if (vnote) {
    return (
      <Alert color="gray" variant="light" p="xs" mt={4}>
        <Group gap={8} wrap="nowrap">
          <IconVideo size={20} stroke={1.5} style={{ flexShrink: 0 }} />
          <Box style={{ minWidth: 0 }}>
            <Text size="xs" fw={600}>
              Видео-кружок из Telegram
            </Text>
            <Text size="xs" c="dimmed">
              Для просмотра используйте telegram-service (polling)
            </Text>
          </Box>
        </Group>
      </Alert>
    )
  }
  return (
    <Text
      size="xs"
      lh={1.45}
      style={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: THREAD_MESSAGE_FONT_SIZE,
        ...bodyStyle,
      }}
    >
      {content || ''}
    </Text>
  )
}

export function mergeWsMessageIntoThread(
  prev: TicketDetailMessage[],
  raw: Record<string, unknown>,
): TicketDetailMessage[] {
  const id = String(raw.id || '')
  if (!id) return prev
  const patch: Partial<TicketDetailMessage> = {
    id,
    channel_delivery_status:
      raw.channel_delivery_status != null
        ? String(raw.channel_delivery_status)
        : undefined,
    delivery_error:
      raw.delivery_error != null ? String(raw.delivery_error) : undefined,
    outbound_content:
      raw.outbound_content != null ? String(raw.outbound_content) : undefined,
    content_locale:
      raw.content_locale != null ? String(raw.content_locale) : undefined,
    content_translated:
      raw.content_translated != null ? String(raw.content_translated) : undefined,
    content: raw.content != null ? String(raw.content) : undefined,
  }
  const idx = prev.findIndex((m) => m.id === id)
  if (idx >= 0) {
    return prev.map((m, i) => (i === idx ? { ...m, ...patch } : m))
  }
  const created = raw as unknown as TicketDetailMessage
  return [...prev, created]
}

export function MessageBubble({
  m,
  currentUserId,
  users,
  quoted,
  onQuote,
  messageActions,
  showTranslation,
  translationEnabled,
}: {
  m: TicketDetailMessage
  currentUserId: string | undefined
  users: WorkspaceUserRow[]
  quoted: TicketDetailQuotedMessage | null
  onQuote?: (msg: TicketDetailMessage) => void
  messageActions?: ReactNode
  showTranslation?: boolean
  translationEnabled?: boolean
}) {
  const { theme } = useTheme()
  const kind = getMessageVisualKind(m, users)
  const isDark = theme === 'dark'

  // Системные события таймлайна — по центру, серым текстом (как служебные метки в чате)
  if (kind === 'system' || isAssignmentSystemPseudoMessage(m)) {
    const when = m.created_at ? formatWhen(m.created_at) : ''
    return (
      <Box
        py={6}
        px="sm"
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Text
          size="xs"
          ta="center"
          lh={1.45}
          maw="min(92%, 520px)"
          style={{
            color: isDark ? 'rgba(180, 186, 196, 0.92)' : 'rgba(110, 118, 130, 0.95)',
            fontStyle: 'italic',
          }}
        >
          {(m.content || '').trim() || 'Системное событие'}
          {when ? (
            <>
              {' · '}
              <Text
                span
                size="xs"
                style={{
                  color: isDark ? 'rgba(140, 146, 156, 0.75)' : 'rgba(140, 146, 156, 0.9)',
                  fontStyle: 'normal',
                  whiteSpace: 'nowrap',
                }}
              >
                {when}
              </Text>
            </>
          ) : null}
        </Text>
      </Box>
    )
  }

  const classic = MESSAGE_CLASSIC[kind]
  const isLeft = classic.align === 'flex-start'
  const isAiKind = kind === 'ai' || kind === 'internal_ai'
  const ch = [m.channel_name, m.channel_type].filter(Boolean).join(' · ')

  const cardBg = isDark ? classic.bgDark : classic.bgLight
  const cardBorder = isDark ? classic.borderDark : classic.borderLight
  const mainTextColor = isDark
    ? classic.darkTextBlackOnBubble
      ? CLASSIC_MSG_TEXT
      : CLASSIC_MSG_TEXT_DARK_ON_BUBBLE
    : CLASSIC_MSG_TEXT
  const mainTextShadow =
    isDark && !classic.darkTextBlackOnBubble ? CLASSIC_MSG_SHADOW_DARK : undefined
  const timeColor = isDark ? CLASSIC_MSG_TIME_DARK : CLASSIC_MSG_TIME_LIGHT
  const quoteBg = isDark
    ? classic.darkTextBlackOnBubble
      ? 'rgba(0, 0, 0, 0.06)'
      : 'rgba(255, 255, 255, 0.08)'
    : 'rgba(0, 0, 0, 0.04)'
  const quoteStripe = isDark ? classic.quoteStripeDark : classic.quoteStripeLight
  const quoteMutedColor =
    isDark && !classic.darkTextBlackOnBubble
      ? 'rgba(255, 255, 255, 0.82)'
      : 'rgba(17, 17, 17, 0.68)'

  let senderLine = m.sender_name?.trim() || ''
  if (kind === 'customer') {
    senderLine = senderLine || 'Клиент'
    if (ch) senderLine = `${senderLine} · ${ch}`
  } else if (kind === 'ai' || kind === 'internal_ai') {
    senderLine = senderLine && senderLine !== 'AI Assistant' ? senderLine : 'Мозгобот'
  } else if (kind === 'internal') {
    senderLine = senderLine || 'Оператор'
  } else {
    senderLine = senderLine || 'Оператор'
  }

  const isMineOperator =
    kind === 'operator' && m.user_id && currentUserId && m.user_id === currentUserId

  const operatorWorkspaceUser =
    (kind === 'operator' || kind === 'internal') && m.user_id
      ? users.find((u) => u.id === m.user_id)
      : undefined
  const operatorAvatarSrc = resolveOperatorAvatarSrc(
    operatorWorkspaceUser
      ? {
          operator_name: displayUser(operatorWorkspaceUser),
          operator_email: operatorWorkspaceUser.email,
          avatar_url: operatorWorkspaceUser.avatar_url,
        }
      : {
          operator_name: m.sender_name,
          operator_email: m.sender_email,
          avatar_url: null,
        },
  )
  const operatorAvatarInitials = operatorWorkspaceUser
    ? workspaceUserInitials(operatorWorkspaceUser)
    : initialsFromSenderFallback(m.sender_name, m.sender_email)

  return (
    <Box
      style={{
        display: 'flex',
        justifyContent: classic.align,
      }}
    >
      <Group
        gap={8}
        align="flex-end"
        wrap="nowrap"
        maw="min(92%, 580px)"
        style={{ flexDirection: 'row' }}
      >
        {kind === 'operator' || kind === 'internal' ? (
          <Avatar
            src={operatorAvatarSrc}
            radius="xl"
            size={28}
            color="blue"
            variant={operatorAvatarSrc ? 'filled' : 'light'}
            style={{ flexShrink: 0, marginBottom: 2 }}
          >
            {operatorAvatarInitials}
          </Avatar>
        ) : null}
        <Card
          padding="xs"
          radius="md"
          withBorder={false}
          style={{
            flex: 1,
            minWidth: 0,
            background: cardBg,
            border: `1px solid ${cardBorder}`,
            boxShadow: isDark && classic.boxShadowDark ? classic.boxShadowDark : undefined,
          }}
        >
          <Group
            gap={6}
            mb={4}
            wrap="wrap"
            align="center"
            justify={
              isAiKind || isLeft || kind === 'operator' || kind === 'internal'
                ? 'flex-start'
                : 'flex-end'
            }
            style={isAiKind ? { width: '100%' } : undefined}
          >
            {kind === 'ai' || kind === 'internal_ai' ? (
              <IconRobot
                size={14}
                stroke={1.5}
                style={{ flexShrink: 0, color: '#8B5CF6', opacity: 0.95 }}
              />
            ) : null}
            {kind !== 'customer' ? (
              <Badge size="xs" variant="light" color={MESSAGE_BADGE_COLOR[kind]}>
                {classic.badge}
              </Badge>
            ) : null}
            {isMineOperator ? (
              <Badge size="xs" variant="outline" color="gray">
                Вы
              </Badge>
            ) : null}
            <Text
              size="xs"
              fw={600}
              lh={1.2}
              style={{
                flex:
                  isLeft || isAiKind || kind === 'operator' || kind === 'internal' ? 1 : undefined,
                minWidth: 0,
                color: mainTextColor,
                textShadow: mainTextShadow,
              }}
              lineClamp={2}
            >
              {senderLine}
            </Text>
            {m.is_bulk ? (
              <Badge size="xs" variant="outline" color="gray">
                Массовая рассылка
              </Badge>
            ) : null}
            <Group
              gap={2}
              wrap="nowrap"
              align="center"
              style={{ flexShrink: 0, ...(isAiKind ? { marginLeft: 'auto' } : {}) }}
            >
              <Text
                size="xs"
                lh={1.2}
                style={{ color: timeColor, flexShrink: 0, textShadow: 'none' }}
              >
                {m.created_at ? formatWhen(m.created_at) : ''}
              </Text>
              {shouldShowChannelDeliveryStatus(m, kind) ? (
                (() => {
                  const deliveryStatus = resolveDisplayDeliveryStatus(m)
                  if (!deliveryStatus) return null
                  return (
                    <MessageChannelDeliveryStatus
                      status={deliveryStatus}
                      translationEnabled={translationEnabled}
                      senderKind={kind === 'ai' ? 'ai' : 'operator'}
                      deliveryError={m.delivery_error}
                      size={13}
                    />
                  )
                })()
              ) : null}
              {onQuote ? (
                <Tooltip label="Цитировать сообщение" withArrow>
                  <ActionIcon
                    type="button"
                    variant="subtle"
                    color="gray"
                    size="xs"
                    aria-label="Цитировать сообщение"
                    onClick={(e) => {
                      e.stopPropagation()
                      onQuote(m)
                    }}
                  >
                    <IconQuote size={11} stroke={1.5} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
              {messageActions}
            </Group>
          </Group>
          {quoted ? (
            <Box
              mb={8}
              p="xs"
              style={{
                borderRadius: 6,
                background: quoteBg,
                borderLeft: `3px solid ${quoteStripe}`,
              }}
            >
              <Group gap={6} wrap="nowrap" mb={6} align="center">
                <IconCornerDownRight
                  size={14}
                  stroke={1.5}
                  style={{ flexShrink: 0, color: quoteStripe }}
                />
                <Text
                  size="xs"
                  fw={600}
                  lineClamp={1}
                  style={{ minWidth: 0, color: mainTextColor, textShadow: mainTextShadow }}
                >
                  {quoteInBubbleAuthorLabel(m, quoted)}
                </Text>
                {quoted.attachments && quoted.attachments.length > 0 ? (
                  <Badge
                    size="xs"
                    variant="filled"
                    color="blue"
                    style={{ flexShrink: 0 }}
                    leftSection={<IconPaperclip size={10} stroke={1.5} />}
                  >
                    {quoted.attachments.length}
                  </Badge>
                ) : null}
              </Group>
              <Tooltip
                label={quoted.content || ''}
                multiline
                maw={420}
                openDelay={400}
                disabled={!(quoted.content || '').trim()}
              >
                <Text
                  size="xs"
                  lh={1.45}
                  lineClamp={3}
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: quoteMutedColor,
                    textShadow: classic.darkTextBlackOnBubble ? 'none' : mainTextShadow,
                  }}
                >
                  {quoted.content || ''}
                </Text>
              </Tooltip>
            </Box>
          ) : null}
          <Box style={{ textAlign: 'left', width: '100%' }}>
            {showTranslation &&
            m.message_type === 'customer' &&
            m.content_locale &&
            m.content_locale !== 'ru' ? (
              <Text size="xs" c="dimmed" mb={4}>
                Оригинал ({LOCALE_LABELS[m.content_locale] || m.content_locale})
              </Text>
            ) : null}
            <MessageBodyContent
              content={m.content || ''}
              bodyStyle={{ color: mainTextColor, textShadow: mainTextShadow }}
            />
            {(() => {
              const trBlock = showTranslation ? translationBlockForMessage(m) : null
              if (trBlock) {
                return (
                  <Box
                    mt={8}
                    pt={8}
                    style={{
                      borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                    }}
                  >
                    <Text size="xs" c="dimmed" mb={4}>
                      {trBlock.label}
                    </Text>
                    <MessageBodyContent
                      content={trBlock.text}
                      bodyStyle={{
                        color: mainTextColor,
                        textShadow: mainTextShadow,
                        opacity: 0.92,
                      }}
                    />
                  </Box>
                )
              }
              if (
                showTranslation &&
                m.message_type === 'customer' &&
                m.content_locale &&
                m.content_locale !== 'ru' &&
                !(m.content_translated || '').trim()
              ) {
                return (
                  <Text size="xs" c="dimmed" mt={6}>
                    Перевод загружается… обновите страницу через несколько секунд.
                  </Text>
                )
              }
              return null
            })()}
          </Box>
          {m.attachments && m.attachments.length > 0 ? (
            <TicketMessageAttachments attachments={m.attachments} alignEnd={!isLeft && !isAiKind} />
          ) : null}
        </Card>
        {kind === 'customer' ? (
          <Avatar
            src={m.customer_avatar ? resolveUploadsUrl(m.customer_avatar) : undefined}
            radius="xl"
            size={28}
            color="orange"
            variant={m.customer_avatar ? 'filled' : 'light'}
            style={{ flexShrink: 0, marginBottom: 2 }}
          >
            {(m.sender_name?.trim() || 'К').charAt(0).toUpperCase()}
          </Avatar>
        ) : null}
      </Group>
    </Box>
  )
}
